import React from 'react';
import { Box, List, ListItem, Skeleton } from '@mui/material';
import { colors } from '@src/styles';

interface TableSkeletonProps {
  rowCount?: number;
}

const SkeletonRow: React.FC = () => {
  return (
    <ListItem
      sx={{
        padding: '8px 12px',
        borderRadius: 1,
        backgroundColor: 'inherit',
        marginBottom: 0.5,
        border: `1px solid ${colors.background.secondary}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
        {/* Organization Id */}
        <Box sx={{ display: 'flex', width: '200px', alignItems: 'center' }}>
          <Skeleton variant="text" width="85%" height={20} sx={{ backgroundColor: colors.background.secondary }} />
        </Box>

        {/* Task Message */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="85%" height={20} sx={{ backgroundColor: colors.background.secondary }} />
        </Box>

        {/* Status */}
        <Box sx={{ display: 'flex', width: '80px', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Skeleton
            variant="rectangular"
            width={36}
            height={36}
            sx={{
              borderRadius: '50%',
              backgroundColor: colors.background.secondary,
            }}
          />
        </Box>

        {/* Created Date */}
        <Box sx={{ display: 'flex', width: '140px', alignItems: 'center' }}>
          <Skeleton variant="text" width="90%" height={18} sx={{ backgroundColor: colors.background.secondary }} />
        </Box>

        {/* Source */}
        <Box sx={{ display: 'flex', width: '120px', alignItems: 'center' }}>
          <Skeleton
            variant="rectangular"
            width="70%"
            height={24}
            sx={{
              borderRadius: '12px',
              backgroundColor: colors.background.secondary,
            }}
          />
        </Box>

        {/* Assigned User */}
        <Box sx={{ display: 'flex', width: '140px', alignItems: 'center' }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={32}
            sx={{
              borderRadius: '16px',
              backgroundColor: colors.background.secondary,
            }}
          />
        </Box>
      </Box>
    </ListItem>
  );
};

const SkeletonHeader: React.FC = () => {
  return (
    <Box
      sx={{
        padding: '8px 12px',
        backgroundColor: colors.background.primary,
        borderRadius: '4px 4px 0 0',
        borderBottom: `1px solid ${colors.background.secondary}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
        <Box sx={{ width: '200px' }}>
          <Skeleton variant="text" width={100} height={24} sx={{ backgroundColor: colors.background.secondary }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={60} height={24} sx={{ backgroundColor: colors.background.secondary }} />
        </Box>
        <Box sx={{ width: '80px' }}>
          <Skeleton variant="text" width={50} height={24} sx={{ backgroundColor: colors.background.secondary }} />
        </Box>
        <Box sx={{ width: '140px' }}>
          <Skeleton variant="text" width={40} height={24} sx={{ backgroundColor: colors.background.secondary }} />
        </Box>
        <Box sx={{ width: '120px' }}>
          <Skeleton variant="text" width={45} height={24} sx={{ backgroundColor: colors.background.secondary }} />
        </Box>
        <Box sx={{ width: '158px' }}>
          <Skeleton variant="text" width={80} height={24} sx={{ backgroundColor: colors.background.secondary }} />
        </Box>
      </Box>
    </Box>
  );
};

export const TableSkeleton: React.FC<TableSkeletonProps> = ({ rowCount = 8 }) => {
  return (
    <Box
      sx={{
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        animation: 'shimmer 2s ease-in-out infinite',
        '@keyframes shimmer': {
          '0%': {
            opacity: 1,
          },
          '50%': {
            opacity: 0.4,
          },
          '100%': {
            opacity: 1,
          },
        },
      }}
    >
      <SkeletonHeader />
      <Box
        sx={{
          overflowY: 'auto',
          flexGrow: 1,
          padding: '8px',
        }}
      >
        <List sx={{ padding: 0 }}>
          {Array.from({ length: rowCount }).map((_, index) => (
            <SkeletonRow key={index} />
          ))}
        </List>
      </Box>
    </Box>
  );
};
